import classNames from "classnames";

import AscFilesUploader from "../../AscFilesUploader/AscFilesUploader";
import AscRadioButton from "../../AscInputFields/AscRadioButton/AscRadioButton";
import AscTextInput from "../../AscInputFields/AscTextInput/AscTextInput";
import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";
import { TrashIcon } from "../../AscIcons";

import styles from "./AscAddQuizCard.module.scss";
import { ActivityImage, AdminQuizQuestions } from "@ascendnow/shared";
import { If } from "react-conditional-components-renderer";
import { AscRichTextEditor } from "@ascendnow/ascend-components/components/index";

export interface AscAddQuizCardProps {
  className?: string;
  value: Partial<AdminQuizQuestions["questions"]["questions"][number]>;
  image?: ActivityImage;
  onChange: (
    value: Partial<AdminQuizQuestions["questions"]["questions"][number]>
  ) => void;
  onImageUpload?: (value: ActivityImage) => void;
  onImageRemove?: (value: ActivityImage) => void;
  onDelete?: () => void;
  canDelete?: boolean;
  type?: "quiz" | "assessment";
}

export default function AscAddQuizCard({
  className,
  value,
  image,
  onChange,
  onImageUpload,
  onImageRemove,
  onDelete,
  canDelete = false,
  type = "quiz",
}: AscAddQuizCardProps) {
  function handleAnswerChange(
    answer: { value?: string; isTrue?: "true" | "false" },
    index: number
  ) {
    const answers = value?.answers?.map((a, i) => {
      // update the changed answer by index
      if (i === index) {
        return {
          ...a,
          ...answer,
        };
      }

      // if answer.isTrue was set to true it means that the radio button was toggled
      // and therefore other answers should be set to false, if not do nothing
      const isTrue = answer?.isTrue === "true" ? "false" : a?.isTrue;

      return {
        ...a,
        isTrue,
      };
    });
    onChange?.({ answers });
  }

  return (
    <div
      data-testid="asc-add-quiz-card"
      className={classNames(styles.ground, className)}
    >
      <TrashIcon
        className={styles.trashIcon}
        onClick={canDelete ? onDelete : undefined}
      />
      <If test={type === "quiz"}>
        <AscParagraph className={styles.title}>Thumbnail</AscParagraph>
        <AscFilesUploader
          name="quiz.image"
          accept={{
            "image/*": [".jpeg", ".png"],
          }}
          multiple={false}
          onDrop={(files) => onImageUpload?.(files?.[0])}
          onRemoveFile={() => image && onImageRemove?.(image)}
          initialValue={image && !image?.__activityImage ? [image] : undefined}
          srcFile={
            image
              ? image?.__activityImage
                ? image?.__activityImage?.location
                : URL.createObjectURL(image)
              : undefined
          }
        />
      </If>

      <div className={styles.form}>
        <div className={styles.fieldForm}>
          <label className={styles.fieldLabel} htmlFor="question">
            Question
          </label>
          <AscTextInput
            id="question"
            fullWidth={true}
            placeholder="Question"
            onChange={(e) => onChange?.({ question: e?.target?.value })}
            value={value?.question}
          />
        </div>

        <If test={type === "assessment"}>
          <div className={styles.fieldForm}>
            <label className={styles.fieldLabel} htmlFor="answerExplanation">
              Answer explanation
            </label>
            <AscRichTextEditor
              onHtmlChange={(value) => onChange?.({ answerExplanation: value })}
              className={styles.richTextEditor}
              inputWrapperClassname={styles.richTextEditorInput}
              id="answerExplanation"
              placeholder="Answer explanation"
              initialInferValue={value?.answerExplanation}
            />
          </div>
        </If>

        <div className={styles.fieldForm}>
          <label className={styles.fieldLabel} htmlFor="answer">
            Answer
          </label>
          {value?.answers?.map((answer, index) => (
            <div className={styles.fieldWithRadio}>
              <div className={styles.fieldAnswer}>
                <AscTextInput
                  key={index}
                  fullWidth={true}
                  placeholder="Answer"
                  id="answer"
                  onChange={(e) =>
                    handleAnswerChange(
                      {
                        value: e.target.value,
                      },
                      index
                    )
                  }
                  value={answer?.value}
                />
                <AscRadioButton
                  onChange={(e) =>
                    handleAnswerChange(
                      {
                        isTrue: e.target.checked ? "true" : "false",
                      },
                      index
                    )
                  }
                  checked={answer?.isTrue === "true"}
                />
              </div>
            </div>
          ))}
        </div>

        <AscParagraph className={styles.text}>
          Once you have entered the answer choices, use the radio button to
          select the correct answer.{" "}
        </AscParagraph>
      </div>
    </div>
  );
}
